import React, { useEffect, useRef } from 'react';
import { Dialog } from 'radix-ui';
import { SmartScheduleEvent } from '@/features/smartSchedule/model';
import { computeQuickMoveWarnings, createQuickMoveDraft, formatQuickMoveClock, formatQuickMoveDate, QuickMoveDraft, QuickMoveSource, summarizeQuickMoveWarningsForConfirm } from '@/features/scheduleQuickMove';
import { useQuickMoveSubmission } from './useQuickMoveSubmission';

type Props = {
    source: QuickMoveSource;
    initialDraft: QuickMoveDraft;
    centerId: string;
    authorization: string;
    slotEvents: SmartScheduleEvent[];
    onClose: () => void;
    onSuccess: (draft: QuickMoveDraft) => void;
    onEdit: (draft: QuickMoveDraft) => void;
    blockedMessage?: string;
};

export const QuickMoveConfirm = ({ source, initialDraft, centerId, authorization, slotEvents, onClose, onSuccess, onEdit, blockedMessage }: Props) => {
    const { draft, displayNotice, isFinished, isSaving, validation, preview, overlaps, overlapMessage, disabled, previewUnavailable, lock, submit, close, actionLock } =
        useQuickMoveSubmission({ source, initialDraft, centerId, authorization, onClose, onSuccess, blockedMessage });
    const contentRef = useRef<HTMLDivElement>(null);
    const submitRef = useRef<HTMLButtonElement>(null);
    const original = createQuickMoveDraft(source);
    const { warnings } = validation.ok ? computeQuickMoveWarnings({ source, target: validation, slotEvents }) : { warnings: [] };
    const warningSummary = summarizeQuickMoveWarningsForConfirm(source, warnings);
    const saveDisabled = disabled || previewUnavailable || !validation.ok || validation.unchanged;
    const previewLabel = preview.isLoading ? '일정 확인 중…' : '일정 확인이 안 돼요';
    // 일정 확인 중에는 창에 포커스를 두고, 확인이 끝나면 옮기기 버튼으로 옮긴다.
    useEffect(() => {
        if (!saveDisabled && document.activeElement === contentRef.current) submitRef.current?.focus();
    }, [saveDisabled]);
    const edit = () => {
        // 시트로 전환하며 로컬 저장 판정이 초기화되어도 재요청 잠금이 풀리지 않게 한다.
        if (!lock.current && !isSaving && !isFinished) onEdit(draft);
    };

    return (<>
        {actionLock.gate}
        {/* 잠금 카드가 떠 있는 동안 창을 잠시 닫는다 — Radix 모달이 포커스를 가둬 비밀번호를 칠 수 없다(입력값은 위 훅에 남는다) */}
        <Dialog.Root open={!actionLock.isAsking} onOpenChange={(open) => { if (!open) close(); }}>
            <Dialog.Portal>
                <Dialog.Overlay className="quick-move-overlay" />
                <Dialog.Content className="quick-move-confirm" ref={contentRef}
                    onOpenAutoFocus={(event) => { event.preventDefault(); (saveDisabled ? contentRef.current : submitRef.current)?.focus(); }}
                    onEscapeKeyDown={(event) => { if (lock.current || isSaving) event.preventDefault(); }}
                    onPointerDownOutside={(event) => event.preventDefault()}>
                    <form onSubmit={submit} aria-busy={isSaving} onKeyDown={(event) => {
                        // 누르고 있는 Enter 의 반복 입력은 버튼 기본 제출까지 막는다(실패 직후 재전송 방지).
                        if (event.key === 'Enter' && event.repeat) { event.preventDefault(); return; }
                        if (event.key === 'Enter' && !event.nativeEvent.isComposing && !(event.target as HTMLElement).closest('button[type="button"]')) void submit(event);
                    }}>
                        <Dialog.Title title={`${source.title} 옮길까요?`}>{source.title} 옮길까요?</Dialog.Title>
                        <Dialog.Description className="quick-move-confirm-time">
                            {formatQuickMoveDate(original.date)} {formatQuickMoveClock(original.startMinutes)} → {original.date !== draft.date && `${formatQuickMoveDate(draft.date)} `}<strong>{formatQuickMoveClock(draft.startMinutes)}</strong>
                        </Dialog.Description>
                        {!displayNotice && warningSummary && <p className="quick-move-confirm-warnings">{warningSummary}</p>}
                        {displayNotice && <div className={`quick-move-notice is-${displayNotice.tone}`} role="status" aria-label="변경 결과 안내"><strong>{displayNotice.title}</strong><p>{displayNotice.message}</p>{displayNotice.showRefresh && <button type="button" onClick={() => window.location.reload()}>새로고침</button>}</div>}
                        {!displayNotice && overlaps.length > 0 && <p className="quick-move-overlap-warning" role="alert">{overlapMessage}</p>}
                        {!validation.ok && <p className="quick-move-error" role="alert">{validation.message}</p>}
                        {blockedMessage && <p className="quick-move-error" role="alert">{blockedMessage}</p>}
                        <footer>
                            <button type="button" disabled={isSaving} onClick={close}>{isFinished ? '닫기' : '취소'}</button>
                            <button type="submit" ref={submitRef} disabled={saveDisabled}>{isSaving ? '변경 확인 중…' : previewUnavailable ? previewLabel : '옮기기'}</button>
                            <button type="button" className="quick-move-confirm-edit" disabled={isSaving || isFinished} onClick={edit}>자세히 수정</button>
                        </footer>
                    </form>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    </>);
};
